import {
  PipelineConfigModeOption,
  PipelineSettingsFormFields,
  PipelineSettingsFormState
} from '../interfaces/pipeline-settings-form-state';
import {
  PipelineDestinationTableSettings,
  PipelineLoadMode,
  PipelineReplicationTypeMode,
  PipelineSchemaEvolution,
  PipelineTransactionalFailureAction
} from '../../../shared/interfaces/integration';
import { DEFAULT_SYNC_FREQUENCY_VALUE } from './sync-frequency';
import { isAllowColumnLevelChangesOnlyEnabled } from '../../../shared/utils/config';
import { SyncType } from '../../interfaces/pipeline-settings-config';
import { GenericKeyValueType } from '../../../shared/interfaces/generics';

export const PIPELINE_SETTINGS_FORM_INITIAL_VALUES: PipelineSettingsFormState = {
  [PipelineSettingsFormFields.PIPELINE_NAME]: '',
  [PipelineSettingsFormFields.DESTINATION_PREFIX]: '',
  [PipelineSettingsFormFields.DESTINATION_PARTITION_KEY]: '',
  [PipelineSettingsFormFields.LOAD_MODE]: PipelineLoadMode.APPEND,
  [PipelineSettingsFormFields.SYNC_TYPE]: SyncType.SCHEDULED,
  [PipelineSettingsFormFields.ERROR_MODE]: PipelineTransactionalFailureAction.PARTIAL,
  [PipelineSettingsFormFields.OBJECT_LEVEL_ERROR_MODE]:
    PipelineTransactionalFailureAction.ALL_OR_NONE,
  [PipelineSettingsFormFields.SCHEMA_EVOLUTION]: PipelineSchemaEvolution.ALLOW_ALL,
  [PipelineSettingsFormFields.SYNC_FREQUENCY]: DEFAULT_SYNC_FREQUENCY_VALUE,
  [PipelineSettingsFormFields.DESTINATION_TABLE_SETTINGS]:
    PipelineDestinationTableSettings.DROP_AND_LOAD,
  [PipelineSettingsFormFields.REPLICATION_TYPE]:
    PipelineReplicationTypeMode.HISTORICAL_AND_INCREMENTAL,
  [PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED]: false
};

export const PIPELINE_APPEND_LOAD_MODE: PipelineConfigModeOption<PipelineLoadMode> = {
  id: PipelineLoadMode.APPEND,
  value: PipelineLoadMode.APPEND,
  label: 'Append',
  description: 'Adds new records to the end of the dataset. Does not modify or delete existing data'
};

export const PIPELINE_MERGE_LOAD_MODE: PipelineConfigModeOption<PipelineLoadMode> = {
  id: PipelineLoadMode.MERGE,
  value: PipelineLoadMode.MERGE,
  label: 'Merge',
  description:
    'Updates existing records with new information if matches are found, or inserts new records if no match exists.'
};

export const PIPELINE_LOAD_MODES = [PIPELINE_MERGE_LOAD_MODE, PIPELINE_APPEND_LOAD_MODE];

export const PIPELINE_LOAD_MODE_DESCRIPTION = PIPELINE_LOAD_MODES.reduce(
  (acc, curr) => {
    return {
      ...acc,
      [curr.value]: curr.description
    };
  },
  {} as GenericKeyValueType<PipelineLoadMode, string>
);

export const PIPELINE_BATCH_ERROR_ALL_OR_NONE_MODE: PipelineConfigModeOption = {
  id: PipelineTransactionalFailureAction.ALL_OR_NONE,
  value: PipelineTransactionalFailureAction.ALL_OR_NONE,
  label: 'Strict',
  description: 'Load data for a Job only if all the Events are successfully processed.',
  isComingSoon: true
};

export const PIPELINE_BATCH_ERROR_PARTIAL_MODE: PipelineConfigModeOption = {
  id: PipelineTransactionalFailureAction.PARTIAL,
  value: PipelineTransactionalFailureAction.PARTIAL,
  label: 'Moderate',
  description: 'Load data for a Job even if some Objects have failed Events.'
};

export const PIPELINE_BATCH_ERROR_MODES = [
  PIPELINE_BATCH_ERROR_ALL_OR_NONE_MODE,
  PIPELINE_BATCH_ERROR_PARTIAL_MODE
];

export const OBJECT_BATCH_ALL_OR_NONE_ERROR_MODE: PipelineConfigModeOption = {
  id: PipelineTransactionalFailureAction.ALL_OR_NONE,
  value: PipelineTransactionalFailureAction.ALL_OR_NONE,
  label: "Don't load data for Objects with failed Events",
  description: 'Load data for an Object only if all the Events are successfully processed in a Job.'
};

export const OBJECT_BATCH_PARTIAL_ERROR_MODE: PipelineConfigModeOption = {
  id: PipelineTransactionalFailureAction.PARTIAL,
  value: PipelineTransactionalFailureAction.PARTIAL,
  label: 'Load data for Objects with less than 10K failed Events',
  description: 'Load data for an Object only if less than 10K Events fail in a Job. '
};

export const OBJECTS_BATCH_ERROR_MODES: PipelineConfigModeOption[] = [
  OBJECT_BATCH_ALL_OR_NONE_ERROR_MODE,
  OBJECT_BATCH_PARTIAL_ERROR_MODE
];

export const PIPELINE_SCHEMA_EVOLUTION_ALLOW_ALL_MODE: PipelineConfigModeOption = {
  id: PipelineSchemaEvolution.ALLOW_ALL,
  value: PipelineSchemaEvolution.ALLOW_ALL,
  label: 'Allow all changes',
  description:
    'Hevo syncs new schemas, tables, and columns in existing tables with the Destination on the fly.'
};

export const PIPELINE_SCHEMA_EVOLUTION_BLOCK_ALL_MODE: PipelineConfigModeOption = {
  id: PipelineSchemaEvolution.BLOCK_ALL,
  value: PipelineSchemaEvolution.BLOCK_ALL,
  label: 'Block all changes',
  description:
    'Hevo does not sync new schemas, tables, and columns in existing tables with the Destination'
};

export const PIPELINE_SCHEMA_EVOLUTION_ALLOW_COLUMN_LEVEL_MODE: PipelineConfigModeOption = {
  id: PipelineSchemaEvolution.ALLOW_COLUMN_LEVEL,
  value: PipelineSchemaEvolution.ALLOW_COLUMN_LEVEL,
  label: 'Allow column-level changes only',
  description:
    'New columns in existing table are synced with the Destination. New schemas and tables are not synced.',
  isComingSoon: !isAllowColumnLevelChangesOnlyEnabled()
};

export const PIPELINE_SCHEMA_EVOLUTION_MODES: PipelineConfigModeOption[] = [
  PIPELINE_SCHEMA_EVOLUTION_ALLOW_ALL_MODE,
  PIPELINE_SCHEMA_EVOLUTION_BLOCK_ALL_MODE,
  PIPELINE_SCHEMA_EVOLUTION_ALLOW_COLUMN_LEVEL_MODE
];

export const PIPELINE_DESTINATION_TABLE_SETTING_DROP_AND_LOAD: PipelineConfigModeOption<PipelineDestinationTableSettings> =
  {
    id: PipelineDestinationTableSettings.DROP_AND_LOAD,
    value: PipelineDestinationTableSettings.DROP_AND_LOAD,
    label: 'Drop table and load data',
    description:
      'Drop the existing Destination table and re-create the table before loading data into it. Select this option when the Source and Destination table schemas are different',
    banner: {
      title: 'Possible Data Loss at Destination',
      color: 'warning',
      icon: 'warning',
      description:
        'Hevo drops the existing Destination table, which may lead to a loss of Destination data.'
    }
  };

export const PIPELINE_DESTINATION_TABLE_SETTING_TRUNCATE_AND_LOAD: PipelineConfigModeOption<PipelineDestinationTableSettings> =
  {
    id: PipelineDestinationTableSettings.TRUNCATE_AND_LOAD,
    value: PipelineDestinationTableSettings.TRUNCATE_AND_LOAD,
    label: 'Truncate and load',
    description:
      'Drop all data from the existing Destination table before loading new data into it. Select this option when the Source and Destination table schemas are the same',
    isComingSoon: true
  };

export const PIPELINE_DESTINATION_TABLE_SETTING_LOAD_AS_IS: PipelineConfigModeOption<PipelineDestinationTableSettings> =
  {
    id: PipelineDestinationTableSettings.LOAD_AS_IS,
    value: PipelineDestinationTableSettings.LOAD_AS_IS,
    label: 'Load data as-is',
    description:
      'Load data into the existing Destination table. Select this option when the Schema and Destination table schemas are the same',
    isComingSoon: true
  };

export const PIPELINE_DESTINATION_TABLE_SETTINGS_MODES = [
  PIPELINE_DESTINATION_TABLE_SETTING_DROP_AND_LOAD,
  PIPELINE_DESTINATION_TABLE_SETTING_TRUNCATE_AND_LOAD,
  PIPELINE_DESTINATION_TABLE_SETTING_LOAD_AS_IS
];
